import {Tooltip} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {ReactNode} from 'react';

import {isNil} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {PriceBreakdown, PriceRow} from '../types/PriceBreakdown';
import {MutedText} from './MutedText';
import {PriceBadge} from './PriceBadge';
import {PriceChangeBadge} from './PriceChangeBadge';

interface PriceCardProps extends TestIdProps {
  /** E.g. "Price with VAT" or "Current bid". */
  label: string;
  /** Null renders a placeholder price, e.g. for an auction lot without a bid yet. */
  breakdown: PriceBreakdown | null;
  /** Null when the ad does not say; the card then shows no VAT badge. */
  isVatDeductible: boolean | Nullish;
  /** E.g. "Incl. 21% VAT (Czech Republic)", shown next to the net price. */
  vatNote?: string | Nullish;
  /** Replaces the price for a sealed-bid auction without exposing a numeric amount. */
  undisclosedMessage?: string;
  /**
   * Change since the price was first scraped, in the card currency: positive is a discount,
   * negative a price increase. Zero or null shows no badge.
   */
  priceChange?: number | Nullish;
  /**
   * Set for a vehicle that is no longer for sale: the card turns grey and a "Sold" badge with this
   * explanation as its tooltip takes the place of the price change badge.
   */
  soldReason?: string | Nullish;
  /** Shown on hover or focus of the price change badge, e.g. the price history. */
  priceChangeDetail?: ReactNode;
  /** Extra content at the bottom of the card. */
  children?: ReactNode;
}

/**
 * The dark price block of a listing preview. The price and the VAT badge, and the net price and
 * the VAT note, sit side by side while they fit and wrap under each other in a narrow preview.
 */
export function PriceCard(props: PriceCardProps) {
  const formatCurrency = useFormatCurrency();

  const format = (amount: number, currency: string) =>
    formatCurrency(amount, currency, 0) ?? EMPTY_PLACEHOLDER;

  const originalLine = (row: PriceRow) =>
    isNotNil(row.original)
      ? `${format(row.original.amount, row.original.currency)} ${i18n.t(
          'entity.sourcing.vehiclePreview.labels.inOriginalCurrency'
        )}`
      : null;

  const grossPrice = props.breakdown?.withVat;
  const priceChangeCurrency = grossPrice?.currency ?? '';
  // Prices are shown in whole units, so a change that rounds to zero would read "0 Kč".
  const roundedPriceChange = isNil(props.priceChange) ? null : Math.round(props.priceChange);
  const priceChange =
    isNil(grossPrice) || isNil(roundedPriceChange) || roundedPriceChange === 0
      ? null
      : roundedPriceChange;
  const soldReason = isNotNilOrEmpty(props.soldReason) ? props.soldReason : null;
  const isSold = isNotNil(soldReason);
  const grossOriginal = isNil(grossPrice) ? null : originalLine(grossPrice);
  let displayedPrice = EMPTY_PLACEHOLDER;
  if (isNotNil(grossPrice)) {
    displayedPrice = format(grossPrice.amount, grossPrice.currency);
  }
  if (isNotNil(props.undisclosedMessage)) {
    displayedPrice = props.undisclosedMessage;
  }

  return (
    <Box
      backgroundColor={isSold ? 'palettes.neutral.400.100' : 'palettes.neutral.900.100'}
      borderRadius="medium"
      padding={4}
      data-testid={props['data-testid']}
    >
      <VStack spacing={3}>
        <HStack justify="space-between" align="flex-end" spacing={3} wrap>
          <VStack spacing={1} minWidth={0}>
            <MutedText>{props.label}</MutedText>
            <Heading
              size={2}
              color="white"
              isSingleLine={isNil(props.undisclosedMessage)}
              data-testid={suffixTestId('price', props)}
            >
              {displayedPrice}
            </Heading>
            <Show when={grossOriginal}>
              {(line) => (
                <MutedText data-testid={suffixTestId('originalPrice', props)}>{line}</MutedText>
              )}
            </Show>
          </VStack>
          {/* Badges on the right; in a narrow card they wrap under the price. */}
          <VStack spacing={2} align="flex-end">
            <Show when={soldReason}>
              {(reason) => (
                // Tooltip makes its wrapper a tab stop, so keyboard users reach the reason too.
                <Tooltip label={reason} placement="top">
                  <PriceBadge
                    label={i18n.t('entity.vehicle.labels.sold')}
                    tone="muted"
                    data-testid={suffixTestId('sold', props)}
                  />
                </Tooltip>
              )}
            </Show>
            <Show when={isSold ? null : priceChange}>
              {(change) => (
                <PriceChangeBadge detail={props.priceChangeDetail}>
                  <PriceBadge
                    label={
                      change > 0
                        ? i18n.t('entity.vehicle.labels.discount', {
                            discount: format(change, priceChangeCurrency),
                          })
                        : i18n.t('entity.vehicle.labels.priceIncrease', {
                            increase: format(-change, priceChangeCurrency),
                          })
                    }
                    tone={change > 0 ? 'warning' : 'danger'}
                    data-testid={suffixTestId('priceChange', props)}
                  />
                </PriceChangeBadge>
              )}
            </Show>
            <Show when={isNotNil(props.isVatDeductible)}>
              <PriceBadge
                label={
                  props.isVatDeductible
                    ? i18n.t('entity.sourcing.vehiclePreview.labels.vatDeductible')
                    : i18n.t('entity.sourcing.vehiclePreview.labels.noVatDeduction')
                }
                tone={props.isVatDeductible ? 'positive' : 'muted'}
                icon={props.isVatDeductible ? 'action/done' : undefined}
                data-testid={suffixTestId('vatFlag', props)}
              />
            </Show>
          </VStack>
        </HStack>
        <Show when={props.breakdown?.withoutVat}>
          {(net) => (
            <>
              <Box borderTop="1px solid" borderColor="palettes.neutral.500.100" />
              <HStack justify="space-between" align="flex-end" spacing={3} wrap>
                <VStack spacing={1} minWidth={0}>
                  <MutedText>
                    {i18n.t('entity.sourcing.vehiclePreview.labels.priceWithoutVat')}
                  </MutedText>
                  <Heading
                    size={3}
                    color="white"
                    isSingleLine
                    data-testid={suffixTestId('priceWithoutVat', props)}
                  >
                    {format(net.amount, net.currency)}
                  </Heading>
                  <Show when={originalLine(net)}>
                    {(line) => (
                      <MutedText data-testid={suffixTestId('originalPriceWithoutVat', props)}>
                        {line}
                      </MutedText>
                    )}
                  </Show>
                </VStack>
                <Show when={props.vatNote}>
                  {(note) => (
                    <MutedText data-testid={suffixTestId('vatNote', props)}>{note}</MutedText>
                  )}
                </Show>
              </HStack>
            </>
          )}
        </Show>
        {props.children}
      </VStack>
    </Box>
  );
}
